/**
 * Theme and colour-vision mode, applied to `<html>`.
 *
 * `tokens.css` keys everything off two attributes on the document element:
 * `[data-theme="light"]` swaps the whole palette, `[data-cvd="deuteranopia"]`
 * and `[data-cvd="protanopia"]` remap UP→blue and DOWN→amber. Both are plain
 * attribute writes, so a change repaints without React touching a single node —
 * which matters because the tree it would otherwise re-render contains the
 * ladder.
 *
 * `system` is a real third choice, not an alias for one of the other two: it
 * follows `prefers-color-scheme` and keeps following it. The product is
 * dark-first (design-system README) so `dark` is the default when nothing is
 * stored.
 */

import { uiStore, THEME_STORAGE_KEY, CVD_STORAGE_KEY } from '@/stores/ui'
import type { CvdMode, ThemeChoice, ThemeResolved } from '@/stores/ui'
import { readStored, writeStored } from './stored'

const THEMES: readonly ThemeChoice[] = ['dark', 'light', 'system']
const CVDS: readonly CvdMode[] = ['none', 'deuteranopia', 'protanopia']

const isTheme = (v: unknown): v is ThemeChoice => THEMES.includes(v as ThemeChoice)
const isCvd = (v: unknown): v is CvdMode => CVDS.includes(v as CvdMode)

const systemQuery = (): MediaQueryList | null =>
  typeof globalThis.matchMedia === 'function'
    ? globalThis.matchMedia('(prefers-color-scheme: light)')
    : null

export function resolveTheme(choice: ThemeChoice): ThemeResolved {
  if (choice !== 'system') return choice
  return systemQuery()?.matches ? 'light' : 'dark'
}

function apply(state: { resolvedTheme: ThemeResolved; cvd: CvdMode }): void {
  const root = globalThis.document?.documentElement
  if (!root) return
  root.setAttribute('data-theme', state.resolvedTheme)
  if (state.cvd === 'none') root.removeAttribute('data-cvd')
  else root.setAttribute('data-cvd', state.cvd)
}

export function setTheme(choice: ThemeChoice): void {
  uiStore.mutate((s) => {
    s.theme = choice
    s.resolvedTheme = resolveTheme(choice)
  })
  writeStored(THEME_STORAGE_KEY, choice)
  apply(uiStore.state)
  uiStore.notify()
}

export function setCvd(mode: CvdMode): void {
  uiStore.mutate((s) => {
    s.cvd = mode
  })
  writeStored(CVD_STORAGE_KEY, mode)
  apply(uiStore.state)
  uiStore.notify()
}

/**
 * Read storage, apply the attributes, and keep following the OS while the choice
 * is `system`. Returns a teardown so a test can install and remove it cleanly.
 *
 * Called from `main.tsx` before the first render: `index.html` hard-codes
 * `data-theme="dark"` so the very first paint is never light-on-its-way-to-dark,
 * and this corrects it in the same task if the user chose otherwise.
 */
export function initTheme(): () => void {
  const theme = readStored(THEME_STORAGE_KEY, isTheme, 'dark')
  const cvd = readStored(CVD_STORAGE_KEY, isCvd, 'none')

  // **`mutate`, not `set`.** `UiState` carries preferences this module does not
  // own — `proMode` since the pro ladder — and a whole-object replace here
  // silently reset whichever of them `initPreferences()` had already read,
  // depending only on the order `main.tsx` happened to call the two.
  uiStore.mutate((s) => {
    s.theme = theme
    s.resolvedTheme = resolveTheme(theme)
    s.cvd = cvd
  })
  apply(uiStore.state)
  uiStore.notify()

  const mq = systemQuery()
  if (!mq?.addEventListener) return () => {}

  const onChange = () => {
    if (uiStore.state.theme !== 'system') return
    uiStore.mutate((s) => {
      s.resolvedTheme = resolveTheme('system')
    })
    apply(uiStore.state)
    uiStore.notify()
  }
  mq.addEventListener('change', onChange)
  return () => mq.removeEventListener('change', onChange)
}
